import { Receipt, Scissors } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { ActiveTaskCell, PendingPaymentItem } from "./getPendingPayments";
import "./PendingPaymentIndicator.scss";

export type { ActiveTaskCell, PendingPaymentItem };

type PendingPaymentIndicatorProps = {
  items?: PendingPaymentItem[];
  activeCell?: ActiveTaskCell | null;
  onSelect?: (item: PendingPaymentItem) => void;
};

function initials(label?: string): string {
  const text = label?.trim() ?? "";
  if (!text) return "?";
  return text.slice(0, 1).toUpperCase();
}

type PendingPaymentRowProps = {
  item: PendingPaymentItem;
  isActive: boolean;
  onSelect?: (item: PendingPaymentItem) => void;
};

function PendingPaymentRow({ item, isActive, onSelect }: PendingPaymentRowProps) {
  const note = item.serNote?.trim() ?? "";

  return (
    <li>
      <button
        type="button"
        className={cn(
          "pending-payment-indicator__row",
          isActive && "pending-payment-indicator__row--active"
        )}
        onClick={() => onSelect?.(item)}
      >
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage src={item.staffAvatar} alt="" />
          <AvatarFallback className="text-xs">
            {initials(item.staffName || item.guestLabel)}
          </AvatarFallback>
        </Avatar>
        <div className="pending-payment-indicator__body">
          <div className="pending-payment-indicator__guest">{item.guestLabel}</div>
          <div className="pending-payment-indicator__service">
            <Scissors
              className="pending-payment-indicator__service-icon"
              strokeWidth={1.75}
              aria-hidden
            />
            <span className="pending-payment-indicator__title">{item.serTitle}</span>
            {note ? (
              <span className="pending-payment-indicator__note" title={note}>
                {note}
              </span>
            ) : null}
          </div>
        </div>
        <span className="pending-payment-indicator__col">{item.columnLabel}</span>
      </button>
    </li>
  );
}

function PendingPaymentIndicator({
  items,
  activeCell = null,
  onSelect,
}: PendingPaymentIndicatorProps) {
  if (!items?.length) return null;

  const count = items.length;

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${count} pending payments`}
          className="pending-payment-indicator__trigger"
        >
          <Receipt
            className="pending-payment-indicator__icon"
            strokeWidth={1.75}
            aria-hidden
          />
          <span className="pending-payment-indicator__count">{count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={12}
        className="pending-payment-indicator__content z-[2000] w-[min(22rem,calc(100vw-1.5rem))] border-gray-200 bg-white p-0 text-foreground shadow-lg"
      >
        <div className="pending-payment-indicator__header">
          Pending Payment
          <span className="pending-payment-indicator__header-count">
            {count} {count === 1 ? "task" : "tasks"}
          </span>
        </div>
        <Separator />
        <div className="pending-payment-indicator__scroll">
          <ul className="pending-payment-indicator__list">
            {items.map((item) => (
              <PendingPaymentRow
                key={`${item.recordIndex}:${item.taskKey}`}
                item={item}
                isActive={
                  activeCell?.recordIndex === item.recordIndex &&
                  activeCell?.taskKey === item.taskKey
                }
                onSelect={onSelect}
              />
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default PendingPaymentIndicator;
